// Header status pill for what Save & Submit will do:
//   "Existing Config" — this exact config already exists in NetSuite; Save & Submit
//                      will bring in that item (build NOTHING — no item, BOM, or
//                      routing) plus its serial tag + additional lines. Takes
//                      PRIORITY in any mode (Favorites, Item Template, or an
//                      unchanged Edit Config launch — anything whose fingerprint
//                      matches an item). Editing the config away from the match
//                      drops back to Editing/New below.
//   "Editing Config" — updates the launched (editable) item in place + refreshes
//                      its BOM/routing name on its SO line (edit mode).
//   "New Config"     — makes a brand-new part + BOM + routing + a new SO line.
// Both the mode AND the existing-item match come from AnswersContext. The fingerprint
// lookup used to live here, but the ProgressBar needs the same answer to decide whether to
// promise points, and two independent lookups could have disagreed - so there is now one
// lookup in the context and every consumer reads it.

import { useAnswers } from "../state/AnswersContext";

export default function ConfigModeBadge() {
  // configStatus already applies the precedence this badge documents: "existing" wins over
  // the launched Edit/New mode, because a configuration matching a NetSuite item builds
  // nothing however cpq2 was opened.
  const { configStatus: status, existingItem: existing } = useAnswers();

  const STYLE = {
    edit: { color: "#b45309", bg: "#fffbeb", icon: "✎", label: "Editing Config" },
    // cpq2's one blue, the dark shade on its tint (styles.css --blue-dark / --blue-tint).
    existing: { color: "#2f5a7a", bg: "#e8f0f6", icon: "≡", label: "Existing Config" },
    // New is the everyday case, so it is the quiet one: slate, not a second green beside
    // Save and Submit (Kevin, 2026-10-13: hard "to focus on what i need"). Editing and
    // Existing change what the submit does, so they keep their colors.
    new: { color: "#3b4757", bg: "#eef1f5", icon: "＋", label: "New Config" },
  }[status];

  const title =
    status === "edit"
      ? "Save & Submit will UPDATE the item on the launched sales-order line."
      : status === "existing"
        ? `This configuration already exists in NetSuite${
            existing?.itemid ? ` as ${existing.itemid}` : ""
          } — Save & Submit will bring in that item (no new part, BOM, or routing will be built).`
        : "Save & Submit will create a NEW part and add it as a new sales-order line.";

  return (
    <span
      className="cpq-config-mode-badge"
      data-mode={status}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
        // a status, not a button: no outline (the outline read as something to click)
        border: "1px solid transparent",
        color: STYLE.color,
        background: STYLE.bg,
      }}
    >
      <span aria-hidden>{STYLE.icon}</span>
      {STYLE.label}
    </span>
  );
}
